import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { TabBar } from './TabBar'
import { UpdatePrompt } from './UpdatePrompt'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { useThreatLevel } from '@/store/threat'
import { threatGlow } from '@/lib/criteria/levels'
import { evaluateCriteria } from '@/lib/criteria/engine'
import { deriveLevelFromAlerts } from '@/lib/nws/status'
import { useStormEscalation } from '@/features/storm/useStormEscalation'
import { useStormStore } from '@/store/storm'
import './app-shell.css'

/**
 * App chrome: a single mobile-first reading column, a status-tinted ambient glow
 * (driven by the current threat level), the bottom tab bar, and the PWA update
 * prompt. Keeps all routing children inside a scroll region with safe-area padding.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const level = useThreatLevel((s) => s.level)
  const { pathname, search } = useLocation()
  const navigate = useNavigate()

  // Tint the ambient background glow + theme-color to match the current threat level.
  useEffect(() => {
    document.body.style.setProperty('--app-glow', threatGlow(level))
  }, [level])

  // ── Storm-Mode auto-entry ───────────────────────────────────────────────────
  // The takeover, from any screen: when a NEW warning becomes active for the user's
  // location, open Storm Mode exactly once (markAutoEntered gates re-yanking). The
  // ?demo= param is carried through so previews land on a populated takeover.
  const { phase, warning, alerts } = useStormEscalation()
  const setLevel = useThreatLevel((s) => s.setLevel)
  const autoEnteredId = useStormStore((s) => s.autoEnteredId)
  const markAutoEntered = useStormStore((s) => s.markAutoEntered)

  // Single source of truth for the location threat level (drives the glow everywhere).
  useEffect(() => {
    setLevel(evaluateCriteria({ alertsLevel: deriveLevelFromAlerts(alerts) }).level)
  }, [alerts, setLevel])
  useEffect(() => {
    if (phase !== 'storm' || !warning || warning.id === autoEnteredId) return
    markAutoEntered(warning.id)
    if (!pathname.startsWith('/storm')) {
      navigate(`/storm${search.includes('demo') ? search : ''}`)
    }
  }, [phase, warning, autoEnteredId, markAutoEntered, navigate, pathname, search])

  // Scroll to top on route change (native-app feel).
  useEffect(() => {
    document.querySelector('.app-scroll')?.scrollTo(0, 0)
  }, [pathname])

  // Pause all animations while the tab/app is backgrounded (battery + jank discipline).
  useEffect(() => {
    const onVis = () => document.body.classList.toggle('anim-hidden', document.hidden)
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  const immersive = pathname.startsWith('/storm') || pathname.startsWith('/map')

  return (
    <div className="app-shell" data-immersive={immersive || undefined}>
      <main className="app-scroll">
        <RouteErrorBoundary pathname={pathname} onGoHome={() => navigate('/')}>
          <div className="app-column route-fade" key={pathname}>
            {children}
          </div>
        </RouteErrorBoundary>
      </main>
      {!immersive && <TabBar />}
      <UpdatePrompt />
    </div>
  )
}
